const PENDING_INVESTOR_SIGNUP_KEY_PREFIX = 'pendingInvestorSignup';
const PENDING_LOCAL_INVESTOR_SIGNUP_KEY_PREFIX = 'pendingLocalInvestorSignup';

type PendingLocalInvestorSignup = {
  username: string;
  inviteToken: string;
  userId: string;
};

type PendingInvestorSignup = {
  username: string;
  inviteToken: string;
  userId?: string;
};

export const POST_CONFIRMATION_SOCKET_TIMEOUT_MESSAGE =
  'PostConfirmation invocation failed due to error Socket timeout';

const getBrowserStorage = (storageName: 'localStorage' | 'sessionStorage'): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[storageName];
  } catch (error) {
    console.error(`Failed to access ${storageName}`, error);
    return null;
  }
};

const getSessionStorage = (): Storage | null => {
  return getBrowserStorage('sessionStorage');
};

const getLocalStorage = (): Storage | null => {
  return getBrowserStorage('localStorage');
};

const normalizeUsername = (username: string) => username.trim().toLowerCase();

const getPendingInvestorSignupStorageKey = (username: string) =>
  [PENDING_INVESTOR_SIGNUP_KEY_PREFIX, encodeURIComponent(normalizeUsername(username))].join(':');

const getPendingLocalInvestorSignupStorageKey = (username: string, inviteToken: string) =>
  [
    PENDING_LOCAL_INVESTOR_SIGNUP_KEY_PREFIX,
    encodeURIComponent(normalizeUsername(username)),
    encodeURIComponent(inviteToken.trim()),
  ].join(':');

export const persistPendingInvestorSignup = ({
  username,
  inviteToken,
  userId,
}: PendingInvestorSignup) => {
  const storage = getLocalStorage();
  const normalizedUsername = normalizeUsername(username);
  const trimmedInviteToken = inviteToken.trim();
  if (!storage || !normalizedUsername || !trimmedInviteToken) {
    return;
  }

  try {
    storage.setItem(
      getPendingInvestorSignupStorageKey(normalizedUsername),
      JSON.stringify({
        inviteToken: trimmedInviteToken,
        userId: userId?.trim() || undefined,
      }),
    );
  } catch (error) {
    console.error('Failed to persist pending investor signup', error);
  }
};

export const getPendingInvestorSignup = ({
  username,
}: Pick<PendingInvestorSignup, 'username'>): Omit<PendingInvestorSignup, 'username'> | null => {
  const storage = getLocalStorage();
  const normalizedUsername = normalizeUsername(username);
  if (!storage || !normalizedUsername) {
    return null;
  }

  try {
    const storedValue = storage.getItem(getPendingInvestorSignupStorageKey(normalizedUsername));
    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue) as {
      inviteToken?: unknown;
      userId?: unknown;
    };
    const inviteToken =
      typeof parsedValue.inviteToken === 'string' ? parsedValue.inviteToken.trim() : '';
    if (!inviteToken) {
      return null;
    }

    return {
      inviteToken,
      userId: typeof parsedValue.userId === 'string' ? parsedValue.userId.trim() : undefined,
    };
  } catch (error) {
    console.error('Failed to read pending investor signup', error);
    return null;
  }
};

export const clearPendingInvestorSignup = ({
  username,
}: Pick<PendingInvestorSignup, 'username'>) => {
  const storage = getLocalStorage();
  const normalizedUsername = normalizeUsername(username);
  if (!storage || !normalizedUsername) {
    return;
  }

  try {
    storage.removeItem(getPendingInvestorSignupStorageKey(normalizedUsername));
  } catch (error) {
    console.error('Failed to clear pending investor signup', error);
  }
};

export const persistPendingLocalInvestorSignup = ({
  username,
  inviteToken,
  userId,
}: PendingLocalInvestorSignup) => {
  const storage = getSessionStorage();
  const normalizedUsername = normalizeUsername(username);
  const trimmedInviteToken = inviteToken.trim();
  const trimmedUserId = userId.trim();
  if (!storage || !normalizedUsername || !trimmedInviteToken || !trimmedUserId) {
    return;
  }

  try {
    storage.setItem(
      getPendingLocalInvestorSignupStorageKey(normalizedUsername, trimmedInviteToken),
      trimmedUserId,
    );
  } catch (error) {
    console.error('Failed to persist pending local investor signup', error);
  }
};

export const getPendingLocalInvestorSignupUserId = ({
  username,
  inviteToken,
}: Omit<PendingLocalInvestorSignup, 'userId'>): string | null => {
  const storage = getSessionStorage();
  const normalizedUsername = normalizeUsername(username);
  const trimmedInviteToken = inviteToken.trim();
  if (!storage || !normalizedUsername || !trimmedInviteToken) {
    return null;
  }

  try {
    const userId = storage.getItem(
      getPendingLocalInvestorSignupStorageKey(normalizedUsername, trimmedInviteToken),
    );

    return userId?.trim() || null;
  } catch (error) {
    console.error('Failed to read pending local investor signup', error);
    return null;
  }
};

export const clearPendingLocalInvestorSignup = ({
  username,
  inviteToken,
}: Omit<PendingLocalInvestorSignup, 'userId'>) => {
  const storage = getSessionStorage();
  const normalizedUsername = normalizeUsername(username);
  const trimmedInviteToken = inviteToken.trim();
  if (!storage || !normalizedUsername || !trimmedInviteToken) {
    return;
  }

  try {
    storage.removeItem(
      getPendingLocalInvestorSignupStorageKey(normalizedUsername, trimmedInviteToken),
    );
  } catch (error) {
    console.error('Failed to clear pending local investor signup', error);
  }
};

export const isPostConfirmationSocketTimeoutMessage = (message?: string | null): boolean =>
  Boolean(message?.includes(POST_CONFIRMATION_SOCKET_TIMEOUT_MESSAGE));
